
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import DesktopNavigation from './navigation/DesktopNavigation';
import MobileNavigation from './navigation/MobileNavigation';
import { useLanguage } from '@/hooks/useLanguage';
import { useTranslation } from '@/lib/i18n';
import { useAuth } from '@/contexts/AuthContext'; 
import { Button } from '@/components/ui/button';
import { LogOut, QrCode } from 'lucide-react';
import { UserStatusIndicator } from '@/components/UserStatusIndicator';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { CommissionRequestsBadge } from '@/components/admin/commissions/CommissionRequestsBadge';
import { MemberCardModal } from '@/components/referral/MemberCardModal';

const NavBar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const { user, member, logout, isAdmin, isMember, isLoading } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleJoin = () => {
    // If already on member-access page, redirect to subscription
    if (location.pathname === '/member-access') {
      navigate('/subscription');
    } else {
      navigate('/member-access');
    }
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <header
      className={cn(
        'sticky top-0 w-full z-40 transition-all duration-200 border-b border-yellow-100',
        isScrolled || !isHomePage ? 'bg-black shadow-md py-2' : 'bg-black/70 backdrop-blur-md py-4'
      )}
      style={{ minHeight: '72px' }} // Stable height to prevent layout shift
    >
      <div className="singu-container flex items-center justify-between h-full">
        <Link 
          to={user && member ? "/dashboard" : "/"} 
          className="font-bold text-2xl flex items-center gap-2 transition-colors duration-200 text-[#FFCF43]"
        >
          <span className="text-3xl font-extrabold tracking-tight px-2 py-1 rounded bg-black/60 border border-[#FFCF43] border-opacity-30 shadow text-[#FFCF43]">
            FT!
          </span>
          <span className="sr-only">First Tuesday</span>
        </Link>
        
        <div className="hidden md:flex items-center gap-4">
          <DesktopNavigation 
            isHomePage={isHomePage} 
            isScrolled={isScrolled}
            user={user}
            member={member}
            isAdmin={isAdmin}
            isMember={isMember}
          />
          
          {/* Stable auth section with min-width to prevent jumping */}
          <div className="min-w-[120px] flex items-center gap-2 justify-end">
            {isLoading ? (
              <div className="w-10 h-10 animate-pulse bg-gray-700 rounded-full" />
            ) : user && member ? (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsCardModalOpen(true)}
                  className="text-[#FFCF43] hover:bg-[#FFCF43]/10"
                  title="Ma carte"
                >
                  <QrCode className="h-5 w-5" />
                </Button>
                <CommissionRequestsBadge />
                <NotificationBell />
                <UserStatusIndicator />
              </>
            ) : (
              <Button 
                onClick={handleJoin}
                className="bg-singu-copper hover:bg-singu-copper/90 text-white transition-colors duration-200"
              >
                {location.pathname === '/member-access' ? 'S\'abonner' : t('join')}
              </Button>
            )}
          </div>
        </div>
        
        <div className="md:hidden">
          <MobileNavigation 
            isHomePage={isHomePage} 
            isScrolled={isScrolled}
            user={user}
            member={member}
            isAdmin={isAdmin}
            isMember={isMember}
            logout={logout}
          />
        </div>
      </div>
      
      <MemberCardModal open={isCardModalOpen} onOpenChange={setIsCardModalOpen} />
    </header>
  );
};

export default NavBar;
